import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import CommonCheckbox from 'common/CheckBox/Checkbox';
import { Controller } from 'react-hook-form';
import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import { themes } from 'theme/themes';
import { brandingInfo } from 'features/server/brandingSlice';
import { useSelector } from 'react-redux';
import { truncDynCell } from 'common/Table/truncDynCell';
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
interface Data {
  [key: string]: any;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface PurchasePaymentInvoicesTableProps {
  form?: any;
  columns: any[];
  rows: any[];
  resetInvoices?: boolean;
  tableData: any;
  handleChangePage: (pageNo: number) => void;
  handleChangeRows: (size: number) => void;
  pageSize: number;
}

export const PurchasePaymentInvoicesTable: React.FC<PurchasePaymentInvoicesTableProps> = ({ columns, rows, form, resetInvoices, tableData, handleChangePage, handleChangeRows, pageSize }) => {
  const { t } = useTranslation();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(pageSize);
  const branding = useSelector(brandingInfo);
  const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
  React.useEffect(() => {
    form.setValue('purchaseInvoices', []);
    rows.forEach((row) => {
      form.resetField(`purchaseInvoices${row.id}`);
    });
  }, [resetInvoices]);
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data,) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage1 = (action: string) => {
    if (tableData) {
      if (action === "first") {
        if (tableData?.pageable?.pageNumber > 0) {
          handleChangePage(0);
        }
      } else if (action === "prev") {
        if (tableData.pageable && tableData?.pageable?.pageNumber > 0) {
          handleChangePage(tableData?.pageable?.pageNumber - 1);
        }
      } else if (action === "next") {
        if (!tableData?.last) {
          handleChangePage(tableData?.pageable?.pageNumber + 1);
        }
      } else if (action === "last") {
        if (!tableData?.last) {
          handleChangePage(tableData?.totalPages - 1);
        }
      }
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    handleChangeRows(parseInt(event.target.value));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, rows],
  );

  return (
    <Box sx={{ width: '100%' }} className='assignTables' id="inventorytable">
      <TableContainer>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
          <TableHead>
            <TableRow>
              {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                <TableCell
                  key={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  padding={column.disablePadding ? 'none' : 'normal'}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, column.id)}
                  >
                    {column.label}
                    {orderBy === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" className='py-3'>
                  <Typography className="font-Saira font-[500] text-[15px] italic" style={{ color: themeColors?.primary }}>{t('noRecordFound')}</Typography>
                </TableCell>
              </TableRow>
            ) : visibleRows.map((row, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;
              return (
                <TableRow hover tabIndex={-1} key={row.id} sx={{ cursor: 'pointer' }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? 'right' : 'left'}
                      padding={column.disablePadding ? 'none' : 'normal'}
                      component={column.id === 'purchaseInvoicesId' ? 'th' : undefined}
                      id={column.id === 'purchaseInvoicesId' ? labelId : undefined}
                      scope={column.id === 'purchaseInvoicesId' ? 'row' : undefined}
                      sx={{ width: column.id === 'purchaseInvoicesId' ? '40px' : 'auto' }}
                    >
                      {column.id === 'purchaseInvoicesId' ? <Controller
                        name={`purchaseInvoices${[row.id]}`}
                        control={form.control}
                        defaultValue={false}
                        render={({ field }) => (<Box sx={{ '& .MuiCheckbox-root': { padding: '0 9px !important' }, '& label': { paddingTop: '4px !important' } }}>
                          <CommonCheckbox
                            {...field}
                            label={''}
                            onChange={(event) => {
                              const isChecked = event.target.checked;
                              field.onChange(isChecked);

                              const currentSelected = form.watch('purchaseInvoices') || [];
                              let updatedSelected;

                              if (isChecked) {
                                // ✅ Add full object with id + balanceAmount
                                updatedSelected = [
                                  ...currentSelected.filter((i: any) => i.id !== row.id),
                                  { id: row.id, balanceAmount: row.balanceAmount },
                                ];
                              } else {
                                // ✅ Remove unselected one
                                updatedSelected = currentSelected.filter((i: any) => i.id !== row.id);
                              }

                              form.setValue('purchaseInvoices', updatedSelected);
                            }}
                            checked={form.watch(`purchaseInvoices${[row.id]}`)}
                            color={themeColors?.primary}
                          />
                        </Box>)}
                      /> : truncDynCell(row[column.id])}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box className='flex items-center px-[10px] justify-between'>
        <span className="hidden-xs mx-[5px] font-Saira font-[400] text-[13px]">
          {t("rowsPerPage")}
        </span>
        <span>
          <FormControl className="m-1" size="small" sx={{ 
            '& .MuiSelect-select': { paddingRight: '0px !important', textAlign: 'left' },
            '& .MuiInputBase-root': { border: '1px solid rgba(0, 0, 0, 0.23) !important' }, 
            '& input': { position: 'absolute !important' }
          }}>
            <Select
              className="font-Saira font-[500] text-[13px] mt-[2px] w-[75px]"
              value={(tableData && tableData?.pageable?.pageSize) || pageSize}
              onChange={(event) =>
                handleChangeRowsPerPage(
                  event as React.ChangeEvent<HTMLInputElement>
                )
              }
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem
                  className="font-Saira font-[500] text-[13px]"
                  key={option}
                  value={option}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </span>
        <span style={{ flex: 1 }}></span>
        <span
          className="mx-[5px] whitespace-nowrap max-w-[300] font-Saira font-[400] text-[13px]"
        >
          {t("page")}
          <strong className="font-Saira mx-[5px] font-[700] text-[14px]">
            {tableData && tableData.pageable
              ? tableData.pageable.pageNumber + 1
              : 1}
          </strong>{" "}
          <span className="visible-responsive"></span>
        </span>
        <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
          {t("outOf")}

          <strong className="mx-[5px] font-Saira font-[700] text-[14px]">
            {tableData && tableData.totalPages ? tableData.totalPages : 1}
          </strong>
        </span>
        <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
          {t("total")}
          {/* )}{" "}  */}
          <strong className="mx-[5px] font-Saira font-[700] text-[14px]">
            {tableData && tableData.totalElements
              ? tableData.totalElements
              : 0}
          </strong>
        </span>
        <span className="mr-2">
          <FirstPageIcon
            fontSize="small"
            onClick={() => handleChangePage1("first")}
            style={{
              color:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.first
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.first
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
        <span className="mr-2">
          <NavigateBeforeIcon
            fontSize="small"
            onClick={() => handleChangePage1("prev")}
            style={{
              color:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.first
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.first
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
        <span className="mr-2">
          <NavigateNextIcon
            fontSize="small"
            onClick={() => handleChangePage1("next")}
            style={{
              color:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.last
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.last
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
        <span className="mr-2">
          <LastPageIcon
            fontSize="small"
            onClick={() => handleChangePage1("last")}
            style={{
              color:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.last
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                  tableData?.content?.length !== 0 &&
                  !tableData.last
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
      </Box>
    </Box>
  );
};
const rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 200];